import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Clock, ArrowRight, Home, ArrowLeft } from 'lucide-react';

const PagamentoCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'approved' | 'pending' | 'rejected'>('loading');

  useEffect(() => {
    // Parâmetros retornados pelo Mercado Pago
    const collectionStatus = searchParams.get('collection_status');
    const preapprovalId = searchParams.get('preapproval_id');
    const externalReference = searchParams.get('external_reference');

    // Determinar status
    if (collectionStatus === 'approved' || preapprovalId) {
      setStatus('approved');
    } else if (collectionStatus === 'pending') {
      setStatus('pending');
    } else if (collectionStatus === 'rejected') {
      setStatus('rejected');
    } else {
      // Se não tiver status claro, verificar se tem preapproval_id (assinatura criada)
      if (preapprovalId) {
        setStatus('approved');
      } else {
        setStatus('pending');
      }
    }
  }, [searchParams]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-700 flex items-center justify-center animate-pulse">
              <Clock className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Processando...</h2>
            <p className="text-gray-400">Aguarde enquanto verificamos seu pagamento</p>
          </div>
        );

      case 'approved':
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Assinatura Confirmada!</h2>
            <p className="text-gray-400 mb-8">
              Parabéns! Sua assinatura foi ativada com sucesso. 
              Você já pode acessar todos os recursos do seu plano.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-protec-gold text-black hover:bg-protec-gold-light"
                onClick={() => navigate('/dashboard')}
              >
                Ir para o Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300"
                onClick={() => navigate('/configuracoes/assinatura')}
              >
                Ver minha assinatura
              </Button>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-12 h-12 text-yellow-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Pagamento Pendente</h2>
            <p className="text-gray-400 mb-8">
              Seu pagamento está sendo processado. Isso pode levar alguns minutos.
              Você receberá um email quando for confirmado.
            </p>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-8 max-w-md mx-auto">
              <p className="text-yellow-400 text-sm">
                <strong>Dica:</strong> Se você pagou com boleto ou PIX, pode levar até 
                alguns minutos para a confirmação. Fique tranquilo!
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-protec-gold text-black hover:bg-protec-gold-light"
                onClick={() => navigate('/dashboard')}
              >
                Ir para o Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300"
                onClick={() => navigate('/')}
              >
                <Home className="w-4 h-4 mr-2" />
                Voltar ao início
              </Button>
            </div>
          </div>
        );

      case 'rejected':
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Pagamento Recusado</h2>
            <p className="text-gray-400 mb-8">
              Infelizmente seu pagamento não foi aprovado. 
              Por favor, tente novamente com outro cartão ou forma de pagamento.
            </p>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-8 max-w-md mx-auto">
              <p className="text-red-400 text-sm">
                <strong>Possíveis motivos:</strong> Cartão sem limite, dados incorretos 
                ou bloqueio do banco. Entre em contato com sua operadora se necessário.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-protec-gold text-black hover:bg-protec-gold-light"
                onClick={() => navigate('/planos')}
              >
                Tentar novamente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300"
                onClick={() => navigate('/')}
              >
                <Home className="w-4 h-4 mr-2" />
                Voltar ao início
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-protec-dark">
      {/* Header Simples */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-protec-dark/95 backdrop-blur-sm border-b border-protec-gold/20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            className="text-gray-400 hover:text-white"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar
          </Button>
          <h1 className="text-xl font-bold text-protec-gold">ProtecBens</h1>
          <div></div>
        </div>
      </header>
      
      <main className="pt-24 pb-16 flex items-center justify-center min-h-[80vh]">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto bg-protec-dark-light border-protec-gold/20">
            <CardContent className="p-12">
              {renderContent()}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PagamentoCallback;

